/**
 * @module createTokens
 *
 * @see https://0.vuetifyjs.com/composables/registration/create-tokens
 *
 * @remarks
 * Design token registry with alias resolution and W3C Design Tokens format support.
 *
 * Key features:
 * - Alias resolution with circular reference detection
 * - Nested token flattening with dot notation
 * - W3C Design Tokens format ($value, $type, $description, $extensions)
 * - Path-based resolution (e.g., {colors.blue.500} or colors.blue.500)
 * - Resolution caching for performance
 *
 * Used by useTheme, useLocale, useFeatures, and usePermissions for token-based configuration.
 *
 * @example
 * ```ts
 * import { createTokens } from '@vuetify/v0'
 *
 * const tokens = createTokens({
 *   colors: { primary: '#1976d2' },
 * })
 * console.log(tokens.resolve('{colors.primary}'))
 * ```
 */

// Composables
import { useContext } from '#v0/composables/createContext'
import { createRegistry } from '#v0/composables/createRegistry'
import { createTrinity } from '#v0/composables/createTrinity'
import { useLogger } from '#v0/composables/useLogger'

// Utilities
import { isObject, isString, isUndefined, UNSAFE_KEYS } from '#v0/utilities'

// Types
import type { RegistryContext, RegistryContextOptions, RegistryOptions, RegistryTicket } from '#v0/composables/createRegistry'
import type { ContextTrinity } from '#v0/composables/createTrinity'

export interface TokenAlias<T = unknown> {
  [key: string]: unknown
  $value: T
  $type?: string
  $description?: string
  $extensions?: Record<string, unknown>
  $deprecated?: boolean | string
}

export type TokenPrimitive = string | number | boolean | null | Function

export type TokenValue = TokenPrimitive | TokenAlias | TokenCollection

export interface TokenCollection {
  [key: string]: TokenValue | TokenCollection
}

export type FlatTokenCollection = {
  id: string
  value: TokenValue
}

export interface TokenTicket<Z extends TokenValue = TokenValue> extends RegistryTicket<Z> {}

export interface TokenContext<Z extends TokenTicket> extends RegistryContext<Z> {
  /**
   * Checks if a token is an alias
   *
   * @param token The token to check.
   * @returns True if the token is an alias, false otherwise.
   * @remarks An alias is a string that starts with "{" and ends with "}".
   *
   * @see https://0.vuetifyjs.com/composables/registration/create-tokens
   *
   * @example
   * ```ts
   * const tokens = createTokens({
   *   colors: {
   *     primary: '#3b82f6',
   *     secondary: '{colors.primary}', // Alias reference
   *   },
   * })
   *
   * console.log(tokens.isAlias('{colors.primary}')) // true
   * console.log(tokens.isAlias('#3b82f6')) // false
   * ```
   */
  isAlias: (token: unknown) => token is string
  /**
   * Resolves a token or alias to its value.
   *
   * @param token The token or alias to resolve.
   * @returns The resolved value of the token or alias, or undefined if not found.
   * @remarks This function can resolve nested aliases and supports token paths using dot notation.
   *
   * @see https://0.vuetifyjs.com/composables/registration/create-tokens
   *
   * @example
   * ```ts
   * const tokens = createTokens({
   *   colors: {
   *     primary: '#3b82f6',
   *     secondary: '{colors.primary}', // Alias reference
   *   },
   * })
   *
   * console.log(tokens.resolve('{colors.primary}')) // '#3b82f6'
   * console.log(tokens.resolve('{colors.secondary}')) // '#3b82f6'
   * ```
   */
  resolve: <T = unknown>(token: string | TokenAlias) => T | undefined
}

export interface TokenOptions extends RegistryOptions {
  /**
   * Whether to flatten nested token structures.
   *
   * @default false
   */
  flat?: boolean
  /**
   * An optional prefix to prepend to each token ID during registration.
   *
   * @remarks This is useful for namespacing tokens.
   */
  prefix?: string
}

export interface TokenContextOptions extends TokenOptions, RegistryContextOptions {
  tokens?: TokenCollection
}

/**
 * Creates a new token instance.
 *
 * @param tokens The tokens to use.
 * @param options The options for the token instance.
 * @template Z The type of the token ticket.
 * @template E The type of the token context.
 * @returns A new token instance.
 *
 * @see https://www.designtokens.org/tr/drafts/format/
 * @see https://0.vuetifyjs.com/composables/registration/create-tokens
 *
 * @example
 * ```ts
 * import { createTokens } from '@vuetify/v0'
 *
 * const tokens = createTokens({
 *   colors: {
 *     primary: '#3b82f6',
 *     secondary: '{colors.primary}', // Alias reference
 *   },
 * })
 *
 * console.log(tokens.resolve('{colors.primary}')) // '#3b82f6'
 * console.log(tokens.resolve('{colors.secondary}')) // '#3b82f6'
 * ```
 */
export function createTokens<
  Z extends TokenTicket = TokenTicket,
  E extends TokenContext<Z> = TokenContext<Z>,
> (
  tokens: TokenCollection = {},
  options: TokenOptions = {},
): E {
  const logger = useLogger()
  const registry = createRegistry<Z>({ ...options, events: true })

  const cache = new Map<string, unknown | undefined>()

  // Invalidate the resolution cache on any structural mutation, including a
  // ticket's own `unregister()` — subscribing covers every path uniformly,
  // whereas wrapping the context methods misses ticket-level self-removal.
  function invalidate () {
    cache.clear()
  }

  registry.on('register:ticket', invalidate)
  registry.on('unregister:ticket', invalidate)
  registry.on('update:ticket', invalidate)
  registry.on('reindex:registry', invalidate)
  registry.on('clear:registry', invalidate)

  registry.onboard(flatten(tokens, options.prefix, !!options.flat) as Partial<Z>[])

  function isAlias (token: unknown): token is string {
    return isString(token) && token.length > 2 && token[0] === '{' && token.at(-1) === '}'
  }

  function isTokenAlias (value: unknown): value is TokenAlias {
    return isObject(value) && '$value' in value
  }

  function resolve (token: string | TokenAlias, visited = new Set<string>()): unknown | undefined {
    const cacheKey = isString(token) ? token : JSON.stringify(token)

    if (cache.has(cacheKey)) return cache.get(cacheKey)

    const reference: unknown = isTokenAlias(token) ? token.$value : token
    const isAliasReference = isString(reference) && isAlias(reference)

    // A TokenAlias passed directly carries its own value: only a brace-alias
    // $value needs further resolution — a literal or object $value is the value.
    if (isTokenAlias(token) && !isAliasReference) {
      cache.set(cacheKey, reference)
      return reference
    }

    const clean = isAliasReference ? reference.slice(1, -1) : String(reference)

    // Detect circular references
    if (visited.has(clean)) {
      logger.warn(`Circular alias detected for "${clean}"`)
      cache.set(cacheKey, undefined)
      return undefined
    }

    visited.add(clean)

    let found = registry.get(clean)

    let segments: string[] = []
    if (!found && clean.includes('.')) {
      const parts = clean.split('.')
      for (let i = parts.length - 1; i > 0; i--) {
        const prefix = parts.slice(0, i).join('.')
        const suffix = parts.slice(i)
        const candidate = registry.get(prefix)
        if (!isUndefined(candidate?.value)) {
          found = candidate
          segments = suffix
          break
        }
      }
    }

    if (isUndefined(found?.value)) {
      // Only warn if the original token was an alias (wrapped in {})
      if (isAliasReference) {
        logger.warn(`Alias not found for "${String(reference)}"`)
      }
      cache.set(cacheKey, undefined)
      return undefined
    }

    let current: unknown = found.value

    if (segments.length > 0) {
      if (isTokenAlias(current)) current = current.$value

      for (const segment of segments) {
        if (!isObject(current) || UNSAFE_KEYS.has(segment) || !Object.prototype.hasOwnProperty.call(current, segment)) {
          current = undefined
          break
        }

        current = current[segment]

        if (isTokenAlias(current)) current = current.$value
      }

      if (isUndefined(current)) {
        logger.warn(`Path not found inside "${clean}": ${segments.join('.')}`)
        cache.set(cacheKey, undefined)
        return undefined
      }
    } else if (isTokenAlias(current)) {
      current = current.$value
    }

    // A terminal string alias resolves one more hop, whether it came from a
    // segment walk or a leaf value; anything else is the resolved result.
    const result: unknown = isString(current) && isAlias(current)
      ? resolve(current, visited)
      : current

    cache.set(cacheKey, result)

    return result
  }

  return {
    ...registry,
    resolve,
    isAlias,
    get size () {
      return registry.size
    },
  } as E
}

/**
 * Creates a new token context.
 *
 * @param options The options for the token context.
 * @template Z The type of the token ticket.
 * @template E The type of the token context.
 * @returns A new token context.
 *
 * @see https://0.vuetifyjs.com/composables/registration/create-tokens
 *
 * @example
 * ```ts
 * import { createTokensContext } from '@vuetify/v0'
 *
 * export const [useTokens, provideTokens, context] = createTokensContext({
 *  namespace: 'v0:tokens',
 *  tokens: {
 *    colors: {
 *      primary: '#3b82f6',
 *      secondary: '{colors.primary}', // Alias reference
 *    },
 *  },
 * })
 * ```
 */
export function createTokensContext<
  Z extends TokenTicket = TokenTicket,
  E extends TokenContext<Z> = TokenContext<Z>,
> (_options: TokenContextOptions): ContextTrinity<E> {
  const { namespace = 'v0:tokens', tokens = {}, ...options } = _options
  const context = createTokens<Z, E>(tokens, options)

  return createTrinity<E>(namespace, context)
}

/**
 * Returns the current tokens instance.
 *
 * @param namespace The namespace for the tokens context. Defaults to `'v0:tokens'`.
 * @returns The current tokens instance.
 *
 * @see https://0.vuetifyjs.com/composables/registration/create-tokens
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 *   import { useTokens } from '@vuetify/v0'
 *
 *   const tokens = useTokens()
 * </script>
 * ```
 */
export function useTokens<
  Z extends TokenTicket = TokenTicket,
  E extends TokenContext<Z> = TokenContext<Z>,
> (namespace = 'v0:tokens'): E {
  return useContext<E>(namespace)
}

/**
 * Flattens a nested collection of tokens into a flat array of tokens.
 * Each token is represented by an object containing its ID & value.
 * @param tokens The collection of tokens to flatten.
 * @param prefix An optional prefix to prepend to each token ID.
 * @returns An array of flattened tokens, each with an ID and value.
 */
/* #__NO_SIDE_EFFECTS__ */
export function flatten (tokens: TokenCollection, prefix = '', flat = false): FlatTokenCollection[] {
  const flattened: FlatTokenCollection[] = []
  const stack: { tokens: TokenCollection, prefix: string, flat: boolean }[] = [{ tokens, prefix, flat }]

  while (stack.length > 0) {
    const { tokens: currentTokens, prefix: currentPrefix, flat } = stack.pop()!

    const meta: Record<string, unknown> = {}
    for (const k in currentTokens) {
      if (!Object.hasOwn(currentTokens, k)) continue
      if (k.startsWith('$')) meta[k] = (currentTokens as Record<string, unknown>)[k]
    }

    if (Object.keys(meta).length > 0 && currentPrefix) {
      flattened.push({ id: currentPrefix, value: meta as TokenValue })
    }

    for (const key in currentTokens) {
      // Skip inherited keys and prototype-pollution sinks so a polluted
      // Object.prototype (or a caller-supplied `constructor`/`prototype` token)
      // can't leak in as a registered token id. Mirrors mergeDeep.
      if (!Object.hasOwn(currentTokens, key)) continue
      if (UNSAFE_KEYS.has(key)) continue
      if (key.startsWith('$')) continue

      const value = (currentTokens as Record<string, unknown>)[key]
      const id = currentPrefix ? `${currentPrefix}.${key}` : key

      if (!isObject(value)) {
        flattened.push({ id, value: value as string | boolean | number })
        continue
      }

      if ('$value' in value) {
        flattened.push({ id, value: value as TokenAlias })

        // Pinned to unknown: isObject's index type is `any`, so reading through
        // it unpinned would make the guards below compile-optional.
        const inner: unknown = value.$value
        if (isObject(inner) && !flat) {
          for (const innerKey in inner) {
            if (!Object.hasOwn(inner, innerKey)) continue
            if (UNSAFE_KEYS.has(innerKey)) continue
            if (innerKey.startsWith('$')) continue

            const child: unknown = inner[innerKey]
            const childId = `${id}.${innerKey}`
            if (!isObject(child)) {
              flattened.push({ id: childId, value: child as string | boolean | number })
            } else if ('$value' in child) {
              flattened.push({ id: childId, value: child as TokenAlias })
            } else {
              stack.push({ tokens: child as TokenCollection, prefix: childId, flat })
            }
          }
        }
        continue
      }

      if (flat) {
        flattened.push({ id, value: value as TokenValue })
        continue
      }

      stack.push({ tokens: value as TokenCollection, prefix: id, flat })
    }
  }

  return flattened
}
